import i18n from 'src/i18n'
import { TIMEZONE_MODES } from 'src/models/model/constants'
import Model from 'src/models/model/model'
import { IsoDate, IsoDateTime } from 'src/models/model/primitive-types'
import { timezoneMode } from 'src/models/model/reactive-state'

/**
 * Format the time based on the global timezone mode setting.
 * @param {string | Date | IsoDateTime} time - The time to format.
 * @param {string} [format='medium'] - The format to use.
 * @param {TIMEZONE_MODES} [forceMode=null] - The timezone mode to use.
 * @param {boolean} [allowRelative=true] - Whether to allow relative time.
 * @returns {string} The formatted time.
 */
export function formatTime (time, format = 'medium', forceMode = null, allowRelative = true) {
  if (!time) return null

  const mode = forceMode || timezoneMode.value

  if (mode === TIMEZONE_MODES.RELATIVE && allowRelative) {
    return new IsoDateTime(time).relativeTime()
  }

  if (mode === TIMEZONE_MODES.SITE || !allowRelative) {
    time = Model.configuration.currentSite.inSiteTime(time)
  } else if (mode === TIMEZONE_MODES.LOCAL) {
    time = new IsoDateTime(time)
  } else {
    throw new Error(`Unknown timezone mode: ${mode}`)
  }

  if (format === 'dash') {
    return IsoDate.parse(time).format('YYYY-MM-DD')
  }

  return i18n.global.d(time, format)
}

/**
 * Get either a truncated or full version of the time unit label.
 * @param {string} unit - Name of the time unit.
 * @param {boolean} [compact] - When true, return a compact version of the time unit label.
 * @returns {string} The time unit label.
 */
export function timeLabel (unit, compact = false) {
  if (!compact) return unit

  switch (unit) {
    case 'second':
      return 'sec'
    case 'minute':
      return 'min'
    case 'hour':
      return 'hr'
    case 'day':
      return 'day'
    case 'month':
      return 'mon'
    case 'year':
      return 'yr'
    default:
      throw new Error('Unhandled unit ' + unit)
  }
}
